import axios from 'axios';
import { getAuthHeader } from './authService';

const API_URL = `${process.env.REACT_APP_SERVER_URL}/api/proposals`;

const headers = () => getAuthHeader();

export type ProposalLineItem = {
  id: number;
  proposal_id: number;
  ad_slot_id: number;
  label: string;
  size: string | null;
  price: string | number;
  notes: string | null;
  sort_order: number;
  ad_slot?: {
    id: number;
    label: string;
    size?: string;
    price?: string | number;
    issue?: {
      title: string;
      publication?: { id: number; name: string };
    };
  };
};

export type Proposal = {
  id: number;
  company_id: number | null;
  sales_rep_id: number;
  status: 'draft' | 'sent' | 'signed' | 'cancelled' | 'archived';
  contact_name: string | null;
  contact_email: string | null;
  currency: string;
  notes: string | null;
  contract_from: number;
  terms_and_conditions: string | null;
  remind_at: string | null;
  sent_at: string | null;
  archived_at: string | null;
  createdAt: string;
  updatedAt: string;
  total_price?: number;
  line_count?: number;
  company?: { id: number; name: string };
  sales_rep?: { id: number; username: string; email: string };
  line_items?: ProposalLineItem[];
};

export type ProposalCompanyContact = {
  id: number;
  company_id: number;
  first_name: string;
  last_name?: string | null;
  email?: string | null;
  job_title?: string | null;
  is_primary?: boolean;
};

export const getProposals = async (params?: {
  status?: string;
  archived?: string;
  company_id?: string;
  publication_id?: string;
  issue_id?: string;
  sales_rep_id?: string;
}) => {
  try {
    const response = await axios.get(API_URL, { headers: headers(), params });
    return response.data as Proposal[];
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const getProposal = async (id: string) => {
  const response = await axios.get(`${API_URL}/${id}`, { headers: headers() });
  return response.data as Proposal;
};

export const getProposalCompanyContacts = async (companyId: string | number) => {
  const response = await axios.get(`${API_URL}/company-contacts/${companyId}`, { headers: headers() });
  return response.data as ProposalCompanyContact[];
};

export const createProposal = async (data: {
  company_id?: number | null;
  contact_name?: string;
  contact_email?: string;
  currency?: string;
  notes?: string;
  contract_from: number;
  terms_and_conditions?: string;
  line_items?: { ad_slot_id: number; price?: number; notes?: string }[];
  remind_at?: string | null;
}) => {
  const response = await axios.post(API_URL, data, { headers: headers() });
  return response.data as Proposal;
};

export const updateProposal = async (
  id: number,
  data: {
    company_id?: number | null;
    contact_name?: string;
    contact_email?: string;
    currency?: string;
    notes?: string;
    contract_from?: number;
    terms_and_conditions?: string;
    remind_at?: string | null;
  }
) => {
  const response = await axios.put(`${API_URL}/${id}`, data, { headers: headers() });
  return response.data as Proposal;
};

export const addProposalLine = async (
  proposalId: number,
  ad_slot_id: number,
  price?: number,
  notes?: string
) => {
  const response = await axios.post(
    `${API_URL}/${proposalId}/lines`,
    { ad_slot_id, price, notes },
    { headers: headers() }
  );
  return response.data as Proposal;
};

export const updateProposalLine = async (
  proposalId: number,
  lineId: number,
  price: number,
  notes: string
) => {
  const response = await axios.put(
    `${API_URL}/${proposalId}/lines/${lineId}`,
    { price, notes },
    { headers: headers() }
  );
  return response.data as Proposal;
};

export type DealActivity = {
  id: number;
  action: string;
  summary: string;
  metadata?: Record<string, unknown>;
  createdAt: string;
  user?: { id: number; username: string; email: string } | null;
};

export const getProposalActivity = async (proposalId: string | number): Promise<DealActivity[]> => {
  try {
    const response = await axios.get(`${API_URL}/${proposalId}/activity`, { headers: headers() });
    return response.data;
  } catch (error: any) {
    console.log(error.message);
    return [];
  }
};

export const removeProposalLine = async (proposalId: number, lineId: number) => {
  const response = await axios.delete(`${API_URL}/${proposalId}/lines/${lineId}`, {
    headers: headers(),
  });
  return response.data as Proposal;
};

export const sendProposal = async (id: number) => {
  const response = await axios.post(`${API_URL}/${id}/send`, {}, { headers: headers() });
  return response.data as Proposal;
};

export const archiveProposal = async (id: number) => {
  await axios.post(`${API_URL}/${id}/archive`, {}, { headers: headers() });
};

export const cancelProposal = async (id: number) => {
  const response = await axios.post(`${API_URL}/${id}/cancel`, {}, { headers: headers() });
  return response.data as Proposal;
};

export const deleteProposal = async (id: number) => {
  await axios.delete(`${API_URL}/${id}`, { headers: headers() });
};
